import { z } from "zod";

export const MAX_FILE_UPLOAD_COUNT = 25 as const;

export const LLM_ACCEPTED_IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "webp"];

const sectionKeyRegex = /^sections\/.+\.(?:liquid|json)$/;

export const sectionAssetSchema = z
  .object({
    key: z.string().regex(sectionKeyRegex),
    public_url: z.string().nullable(),
    created_at: z.string(),
    updated_at: z.string(),
    content_type: z.string(),
    size: z.number(),
    checksum: z.string(),
    theme_id: z.number(),
  })
  .describe("SectionAsset");

export const sectionSchema = z
  .object({
    key: z.string().regex(sectionKeyRegex),
    themeId: z.string(),
    publicUrl: z.string().nullable(),
    size: z.number(),
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .describe("Section");

export const shopifyAssetSchema = z
  .object({
    id: z.string(),
    createdAt: z.string(),
    updatedAt: z.string(),
    publicUrl: z.string(),
    name: z.string(),
    type: z.enum(["image", "video", "other"]),
    width: z.int().nullish(),
    height: z.int().nullish(),
    fileSize: z.int(),
    altText: z.string().nullable(),
  })
  .describe("ShopifyAsset");

export const assetTypeSchema = z
  .enum(["image", "video", "font", "document", "audio"])
  .describe("AssetType");

/**
 * Canonical list of MIME types the asset library accepts (uploads, presigned
 * PUTs, kind detection, client validation). Declared as a tuple so the union
 * type stays exact and `SUPPORTED_ASSET_MIME_TYPES` is well-typed without
 * `as` casts. Keep in sync with the chat upload list in
 * `apps/replo-agent/src/agent-ui/features/chat/constants.ts` so the assets
 * miniapp and the bedrock-api never drift.
 */
export const SUPPORTED_ASSET_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/jpg",
  "image/webp",
  "image/avif",
  "image/gif",
  "image/svg+xml",
  "video/mp4",
  "video/quicktime",
  "video/webm",
  "audio/mpeg",
  "audio/wav",
  "audio/ogg",
  "audio/mp4",
  "audio/aac",
  "font/woff",
  "font/woff2",
  "font/ttf",
  "font/otf",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/rtf",
  "application/vnd.oasis.opendocument.text",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/epub+zip",
  "application/postscript",
  "text/plain",
  "text/markdown",
  "text/html",
  "text/csv",
  "text/tab-separated-values",
  "application/json",
  "application/xml",
] as const;

export type AssetMimeType = (typeof SUPPORTED_ASSET_MIME_TYPES)[number];

/**
 * Canonical MIME → file-extension table for everything the asset library
 * accepts. `Record<AssetMimeType, string>` makes the table exhaustive — if a
 * new entry is added to `SUPPORTED_ASSET_MIME_TYPES`, this object stops
 * type-checking until the matching extension is filled in.
 */
export const ASSET_MIME_TO_EXTENSION: Record<AssetMimeType, string> = {
  "image/png": "png",
  "image/jpeg": "jpeg",
  "image/jpg": "jpg",
  "image/webp": "webp",
  "image/avif": "avif",
  "image/gif": "gif",
  "image/svg+xml": "svg",
  "video/mp4": "mp4",
  "video/quicktime": "mov",
  "video/webm": "webm",
  "audio/mpeg": "mp3",
  "audio/wav": "wav",
  "audio/ogg": "ogg",
  "audio/mp4": "m4a",
  "audio/aac": "aac",
  "font/woff": "woff",
  "font/woff2": "woff2",
  "font/ttf": "ttf",
  "font/otf": "otf",
  "application/pdf": "pdf",
  "application/msword": "doc",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    "docx",
  "application/rtf": "rtf",
  "application/vnd.oasis.opendocument.text": "odt",
  "application/vnd.ms-powerpoint": "ppt",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    "pptx",
  "application/vnd.ms-excel": "xls",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
  "application/epub+zip": "epub",
  "application/postscript": "ai",
  "text/plain": "txt",
  "text/markdown": "md",
  "text/html": "html",
  "text/csv": "csv",
  "text/tab-separated-values": "tsv",
  "application/json": "json",
  "application/xml": "xml",
};

/**
 * Non-canonical MIME spellings we accept on the way in, mapped to the single
 * spelling every downstream format check matches on. `image/jpg` is not a
 * registered media type, so an un-normalized alias silently falls out of sets
 * keyed by the canonical name -- which is how `.jpg` assets stopped reaching
 * the model as images (REPL-30592). Values are `AssetMimeType` so an alias can
 * only ever resolve to a type the library actually supports.
 */
const MIME_TYPE_ALIASES: Record<string, AssetMimeType> = {
  "image/jpg": "image/jpeg",
};

/**
 * Canonical spelling for a MIME type read off an untrusted source (a stored
 * record, a chat attachment, a `Content-Type` header). Lowercases because RFC
 * 2045 media types are case-insensitive, and passes unrecognized types through
 * unchanged so callers keep their own handling for them.
 */
export function normalizeMimeType(mimeType: string): string {
  const lowercased = mimeType.toLowerCase();
  return MIME_TYPE_ALIASES[lowercased] ?? lowercased;
}

/**
 * Short format label a brand design token carries for an image asset. Distinct
 * from `AssetType` (the persisted image/video/audio/font/document bucket) and
 * from `ASSET_MIME_TO_EXTENSION` (which spells JPEG `jpeg`, not `jpg`): these
 * are the labels the DESIGN.md token rows are written with.
 */
export type AssetFormat =
  | "svg"
  | "png"
  | "jpg"
  | "webp"
  | "gif"
  | "avif"
  | "ico"
  | "unknown";

/**
 * Canonical MIME types from `ASSET_MIME_TO_EXTENSION` for the common case where the browser
 * hands us just a filename (some font files report no `file.type` on Safari).
 * Built once at module load by iterating the typed mime-type tuple, so the
 * value side preserves `AssetMimeType` without any `as` cast.
 */
export const ASSET_EXTENSION_TO_MIME: Record<string, AssetMimeType> = (() => {
  const result: Record<string, AssetMimeType> = {};
  for (const mimeType of SUPPORTED_ASSET_MIME_TYPES) {
    // Indexed directly rather than through `normalizeMimeType` so the value
    // side stays `AssetMimeType` instead of widening to `string`.
    result[ASSET_MIME_TO_EXTENSION[mimeType]] =
      MIME_TYPE_ALIASES[mimeType] ?? mimeType;
  }
  return result;
})();

export const SUPPORTED_ASSET_EXTENSIONS: string[] =
  SUPPORTED_ASSET_MIME_TYPES.map(
    (mimeType) => ASSET_MIME_TO_EXTENSION[mimeType],
  );

/**
 * Maps any supported asset MIME type to the persisted `AssetType` enum.
 * Image/video/audio/font are inferred from the MIME prefix; everything else
 * (PDF, Office formats, text/data, design files) lands in `document`.
 */
export function getAssetTypeFromMimeType(
  mimeType: AssetMimeType,
): z.infer<typeof assetTypeSchema> {
  if (mimeType.startsWith("video/")) {
    return "video";
  }
  if (mimeType.startsWith("audio/")) {
    return "audio";
  }
  if (mimeType.startsWith("font/")) {
    return "font";
  }
  if (mimeType.startsWith("image/")) {
    return "image";
  }
  return "document";
}

export function isSupportedAssetMimeType(
  mimeType: string,
): mimeType is AssetMimeType {
  return mimeType in ASSET_MIME_TO_EXTENSION;
}

/**
 * Resolves the display filename to persist for an uploaded asset. Keeps the
 * original name untouched when it already ends in any recognized asset
 * extension (so `photo.jpg` stays `photo.jpg` even though the canonical MIME
 * extension for `image/jpeg` is `jpeg`), and only appends `fileExtension` when
 * the name has no recognized extension. Returns `null` for empty/whitespace
 * names so callers can substitute their own fallback.
 */
export function resolveAssetName({
  originalName,
  fileExtension,
}: {
  originalName: string | undefined;
  fileExtension: string;
}): string | null {
  const trimmed = originalName?.trim();
  if (!trimmed) {
    return null;
  }
  const lastDotIndex = trimmed.lastIndexOf(".");
  const existingExtension =
    lastDotIndex > 0 ? trimmed.slice(lastDotIndex + 1).toLowerCase() : null;
  if (
    existingExtension &&
    Object.prototype.hasOwnProperty.call(
      ASSET_EXTENSION_TO_MIME,
      existingExtension,
    )
  ) {
    return trimmed;
  }
  return `${trimmed}.${fileExtension}`;
}

export const assetSchema = z
  .object({
    id: z.string(),
    name: z.string().max(255),
    createdAt: z.date(),
    updatedAt: z.date(),
    url: z.string().max(512),
    type: assetTypeSchema,
    sizeBytes: z.int(),
    fileExtension: z.string(),
    width: z.int().nullable(),
    height: z.int().nullable(),
    projectId: z.uuid().nullable(),
    altText: z.string().nullable(),
    embeddingText: z.string().nullable(),
    embedding: z.number().array().nullish(),
    tags: z.array(z.string()).default([]),
  })
  .describe("Asset");

/**
 * Canonical asset record returned by the HTTP API. Mirrors `assetSchema`
 * above but uses string ISO timestamps instead of `z.date()` since this is
 * the over-the-wire shape consumed by clients.
 */
export const assetItemSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    url: z.string(),
    type: assetTypeSchema,
    projectId: z.string().nullable(),
    altText: z.string().nullable(),
    label: z.string().nullable(),
    tags: z.array(z.string()),
    fileExtension: z.string(),
    sizeBytes: z.number(),
    width: z.number().nullable(),
    height: z.number().nullable(),
    embeddingText: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
    approvedAt: z.string().nullable(),
    archivedAt: z.string().nullable(),
  })
  .describe("AssetItem");

export const assetStatusFilterSchema = z
  .enum(["approved", "draft", "archived"])
  .describe("AssetStatusFilter");

/**
 * Django-style ordering token: a sortable field, optionally prefixed with `-`
 * for descending. Resolve it with `resolveAssetOrderBy`. Ordering only applies
 * to the deterministic list path (no `searchTerm`); semantic search always
 * ranks by relevance.
 */
export const assetOrderBySchema = z
  .enum(["createdAt", "-createdAt", "updatedAt", "-updatedAt", "name", "-name"])
  .describe("AssetOrderBy");

export type AssetOrderBy = z.infer<typeof assetOrderBySchema>;
export type AssetOrderByField = "createdAt" | "updatedAt" | "name";
export type AssetOrderByResolved = {
  field: AssetOrderByField;
  direction: "asc" | "desc";
};

const ASSET_ORDER_BY: Record<AssetOrderBy, AssetOrderByResolved> = {
  createdAt: { field: "createdAt", direction: "asc" },
  "-createdAt": { field: "createdAt", direction: "desc" },
  updatedAt: { field: "updatedAt", direction: "asc" },
  "-updatedAt": { field: "updatedAt", direction: "desc" },
  name: { field: "name", direction: "asc" },
  "-name": { field: "name", direction: "desc" },
};

/**
 * Split an ordering token (default `-createdAt`) into the column and direction
 * the repository sorts and keyset-paginates on.
 */
export function resolveAssetOrderBy(
  orderBy: AssetOrderBy = "-createdAt",
): AssetOrderByResolved {
  return ASSET_ORDER_BY[orderBy];
}

const isoDateTimeSchema = z.iso.datetime({ offset: true });

export const assetFindRequestSchema = z
  .object({
    ids: z
      .array(z.uuid())
      .min(1)
      .max(100)
      .optional()
      .describe("Restrict to these asset ids (like Django `id__in`)."),
    tags: z
      .array(z.string().min(1))
      .max(16)
      .optional()
      .describe("Tags that every returned asset must contain."),
    searchTerm: z
      .string()
      .min(1)
      .max(200)
      .optional()
      .describe(
        "Semantic search term. When set, results are scored by tag overlap, name match, and embedding similarity.",
      ),
    type: assetTypeSchema
      .optional()
      .describe("Restrict to a single asset type."),
    status: assetStatusFilterSchema
      .optional()
      .describe(
        "Filter by lifecycle: 'approved' (approved_at set, not archived), 'draft' (neither approved nor archived), or 'archived' (archived_at set).",
      ),
    label: z
      .string()
      .min(1)
      .max(255)
      .optional()
      .describe("Restrict to assets carrying this label (used as category)."),
    createdAtGte: isoDateTimeSchema
      .optional()
      .describe("Only assets created at or after this ISO datetime."),
    createdAtLte: isoDateTimeSchema
      .optional()
      .describe("Only assets created at or before this ISO datetime."),
    updatedAtGte: isoDateTimeSchema
      .optional()
      .describe("Only assets updated at or after this ISO datetime."),
    updatedAtLte: isoDateTimeSchema
      .optional()
      .describe("Only assets updated at or before this ISO datetime."),
    orderBy: assetOrderBySchema
      .optional()
      .describe(
        "Sort order for the list path (ignored when searchTerm is set). Defaults to -createdAt (newest first).",
      ),
    cursor: z
      .string()
      .min(1)
      .optional()
      .describe(
        "Keyset pagination cursor from a previous response's `nextCursor`. Ignored when searchTerm is set (semantic search returns a single ranked page).",
      ),
    limit: z.number().int().positive().max(100).default(50).optional(),
    includeSummary: z
      .boolean()
      .optional()
      .describe(
        "When true, the response includes a `summary` block with sidebar facet counts contextual to the other active filters.",
      ),
  })
  .describe("AssetFindRequest");

/**
 * Sidebar summary counts. Each facet group's counts are computed with all
 * other active filters applied but excluding its own filter, so toggling
 * one facet does not collapse the counts in the others.
 */
export const assetSummarySchema = z
  .object({
    status: z.object({
      approved: z.number(),
      draft: z.number(),
      archived: z.number(),
    }),
    type: z.object({
      image: z.number(),
      video: z.number(),
      font: z.number(),
      document: z.number(),
      audio: z.number(),
    }),
    labels: z.array(
      z.object({
        label: z.string(),
        count: z.number(),
      }),
    ),
    tags: z.array(
      z.object({
        tag: z.string(),
        count: z.number(),
      }),
    ),
  })
  .describe("AssetSummary");

export const assetFindResponseSchema = z
  .object({
    assets: z.array(assetItemSchema),
    nextCursor: z
      .string()
      .nullable()
      .describe(
        "Keyset cursor for the next page, or null when there are no more results. Always null for semantic search (searchTerm), which returns a single ranked page.",
      ),
    summary: assetSummarySchema.optional(),
  })
  .describe("AssetFindResponse");

export const assetUpdateRequestSchema = z
  .object({
    assetId: z.uuid(),
    patch: z
      .object({
        altText: z.string().max(255).nullish(),
        tags: z.array(z.string().min(1)).max(32).optional(),
        label: z.string().min(1).max(255).nullish(),
        /**
         * Lifecycle intent. The server derives `approvedAt`/`archivedAt`
         * timestamps from this so clients can't backdate or clock-skew the
         * lifecycle.
         */
        status: assetStatusFilterSchema.optional(),
      })
      .describe("AssetUpdatePatch"),
  })
  .describe("AssetUpdateRequest");

export const assetUpdateResponseSchema = z
  .object({
    asset: assetItemSchema,
  })
  .describe("AssetUpdateResponse");

export const assetLabelOptionsSchema = z
  .enum([
    // --- ICONS ---
    "Icon/Trust",
    "Icon/Payment",
    "Icon/Feature",
    "Icon",

    // --- PHOTOS ---
    "Photo/Lifestyle",
    "Photo/Model",
    "Photo/Customer",
    "Photo/UGC",
    "Photo/Influencer",
    "Photo/Before After",
    "Photo",

    // --- PRODUCT ---
    "Product",
    "Product/Nutrition",
    "Product/Size Chart",
    "Product/Ingredients",

    // --- LOGOS ---
    "Logo/Brand",
    "Logo/Press Logo",
  ])
  .describe("AssetLabelOptions");

export type AssetLabelOptions = z.infer<typeof assetLabelOptionsSchema>;

export const assetLabelDescriptions: Record<AssetLabelOptions, string> = {
  // --- ICONS ---
  "Icon/Trust":
    "Icon representing trust signals (e.g., satisfaction guarantees, secure checkout badges, free shipping/returns, certifications).",
  "Icon/Payment":
    "Icons for accepted payment methods (e.g., Visa, Mastercard, PayPal, Afterpay).",
  "Icon/Feature":
    "Icons highlighting specific product benefits or features (e.g., waterproof, Bluetooth enabled, organic).",
  Icon: "Use this tag when there is no other more specific label that fits.",

  // --- PHOTOS ---
  "Photo/Lifestyle":
    "Product shown in a realistic setting or context, illustrating how it fits into a customer's life.",
  "Photo/Model":
    "Photo featuring a model interacting with, wearing, or using the product.",
  "Photo/Customer":
    "Authentic photo provided by a customer, often used in testimonials or community sections.",
  "Photo/UGC":
    "User-generated content; photos sourced from customers showcasing the product in their own way.",
  "Photo/Influencer":
    "Content created by social media influencers or creators featuring the product, often in a UGC style.",
  "Photo/Before After":
    "Comparison image showing the state before and after using the product to demonstrate effectiveness.",
  Photo: "Use this tag when there is no other more specific label that fits.",

  // --- PRODUCT ---
  Product: "Standard image of the product, showing the main item clearly.",
  "Product/Nutrition":
    "Image of the nutrition facts panel, supplement facts, or full ingredient list.",
  "Product/Size Chart":
    "Visual guide showing product dimensions or apparel sizing information.",
  "Product/Ingredients":
    "Close-up or stylized image focusing on the key ingredients of a product.",

  // --- LOGOS ---
  "Logo/Brand": "The primary logo of the brand.",
  "Logo/Press Logo":
    "Logos of media or brands other than the primary brand. This may be from customer lists, press releases, or other sources.",
};

export const MAX_BACKGROUND_REMOVAL_IMAGE_BYTES = 20 * 1024 * 1024;

export const removeAssetBackgroundInputSchema = z
  .object({
    image: z.object({
      base64Data: z
        .base64()
        .min(1)
        .max(Math.ceil(MAX_BACKGROUND_REMOVAL_IMAGE_BYTES / 3) * 4),
      mimeType: z.string().regex(/^image\/(png|jpeg|webp|gif)$/),
    }),
    assetName: z.string().trim().min(1).max(255).optional(),
  })
  .describe("RemoveAssetBackgroundInput");

export const removeAssetBackgroundOutputSchema = z
  .object({
    assetId: z.uuid(),
    url: z.url(),
    mimeType: z.literal("image/png"),
    sizeBytes: z.number().int().positive(),
  })
  .describe("RemoveAssetBackgroundOutput");
